import { ReactNode } from "react";
import { createPortal } from "react-dom";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { ReactComponent as InfoIcon } from "../assets/icons/info.svg";

const BUTTON_TOOLTIP_STYLES = "max-w-[250px] w-auto z-[99999] !border border-border-primary !text-content-primary !rounded-md !py-1.5 !px-2.5 !bg-surface-primary !shadow-[0px_8px_28px_0px_rgba(20,20,43,0.10)] !whitespace-normal !text-sm !leading-relaxed !opacity-100";

interface ButtonTooltipProps {
  place?: "top" | "bottom" | "left" | "right";
  id?: string;
}

/**
 * A floating tooltip for buttons and interactive elements.
 * Usage: Add data-tooltip-id="button-tooltip" and data-tooltip-content="Your text" to any element.
 * Place <ButtonTooltip /> once in your component tree.
 *
 * Pass a custom `id` to scope an instance to a single surface (e.g. a tooltip
 * placed below an element near the top of a panel, where the shared
 * top-placed singleton would clip off-screen).
 */
export function ButtonTooltip({ place = "top", id = "button-tooltip" }: ButtonTooltipProps) {
  return (
    <ReactTooltip
      id={id}
      className={BUTTON_TOOLTIP_STYLES}
      noArrow
      place={place}
      offset={8}
      delayShow={0}
      positionStrategy="fixed"
      openEvents={{ mouseenter: true }}
      closeEvents={{ mouseleave: true }}
    />
  );
}

interface TooltipProps {
  id: string;
  children: ReactNode;
  className?: string;
  showIcon?: boolean;
  isHtml?: boolean;
}

export function Tooltip({
  id,
  children,
  className = "",
  showIcon = true,
  isHtml = false,
}: TooltipProps) {
  const formatContent = (content: ReactNode): ReactNode => {
    if (isHtml && typeof content === "string") {
      return <span dangerouslySetInnerHTML={{ __html: content }} />;
    }

    // Convert \n to <br> tags for regular text
    if (typeof content === "string") {
      return content.split("\n").map((line, index) => (
        <span key={index}>
          {line}
          {index < content.split("\n").length - 1 && <br />}
        </span>
      ));
    }
    return children;
  };

  // Portal the tooltip to document.body so CSS `zoom` on ancestors (e.g. the
  // Marketing page's ultra-wide scale-up) doesn't capture its fixed-position
  // containing block and shift the tooltip off its anchor.
  const tooltip = (
    <ReactTooltip
      id={id}
      className={`max-w-[300px] w-auto z-[99999] !border border-border-primary !text-content-primary !rounded-md !p-[11px] !bg-surface-primary !shadow-[0px_8px_28px_0px_rgba(20,20,43,0.10)] !whitespace-normal !text-sm !leading-relaxed !opacity-100 ${className}`}
      noArrow
      positionStrategy="fixed"
    >
      {formatContent(children)}
    </ReactTooltip>
  );

  return (
    <>
      {showIcon && (
        <div className="cursor-pointer" data-tooltip-id={id}>
          <InfoIcon />
        </div>
      )}
      {typeof document !== "undefined"
        ? createPortal(tooltip, document.body)
        : tooltip}
    </>
  );
}
